import React, { createContext, useContext, useState, useCallback, useRef, useEffect, useMemo } from 'react';
import api from '../services/api/api';
import { ThreadManager } from '../services/threads/ThreadManager';
import { useModelSelector } from './ModelSelectorContext';
import { useDataConnection } from './DataConnectionContext';
import { useUser } from './UserContext';
import { useChatUI } from './ChatUIContext';
import { sampleChatService } from '../services/mock/sampleChatService';
import { ModelSelection, ModelType } from '../types/models';
import { ThreadService } from '../services/api/threadService';
import { chatThreadToBackend, chatThreadFromBackend, parseMessageContent } from '../utils/fieldMapping';
import { WS_BASE_URL, API_BASE_URL } from '../config';
import { KrybotService } from '../services/api/krybotService';
import type { ChatMessageResponse } from '../types/krybot';
import { CommandHistoryService } from '../services/api/commandHistoryService';
import { mergeKrybotMessageMetadata } from '../utils/krybotChatMetadata';
import { buildProspectingBootstrapMetadata } from '../utils/krybotProspectingMetadata';
import { isProtectedChatThreadUiRole } from '../utils/chatThreadUiRole';
import {
  findReusableEmptyDraft,
  resolveBootstrapThreadName,
  upsertThreadById,
} from '../components/ChatPanel/chatThreadHygiene';
import { filterThreadsByCompanyId } from '../utils/filterThreadsByCompanyId';
import {
  chatTurnMutatedModel,
  chatWorkspaceToolInvocation,
  notifyControlBridgeModelChanged,
} from '../utils/chatWorkspaceToolInvocation';

export interface ChatMessage {
  id: string;
  threadId: string;
  who: 'user' | 'assistant' | 'system';
  text: string;
  timestamp: string;
  type?: 'text' | 'command' | 'card' | 'error' | 'artifact_list' | 'thread_list';
  cards?: any[]; // For storing card data (artifacts, threads, etc.)
  metadata?: {
    command?: string;
    artifactId?: string;
    cardType?: string;
    taskId?: string; // Added for task card support
    error?: string;
    commandResult?: any; // For storing command result data
    artifact?: any; // For storing artifact data in card messages
    items?: any[]; // For storing list items in card messages
    itemType?: string; // For storing item type in card messages
    commandHistory?: {
      command: string;
      args: string[];
      executionTime?: number;
      dataSource?: string;
      success?: boolean;
      error?: string;
    };
    command_suggestions?: Array<{
      command: string;
      args: string[];
      full_text: string;
      position: number;
    }>; // Command suggestions from Krybot agent
    /** Parsed from assistant reply (Krybot `parse_response`) — show as snapshot card on prospector */
    artifact_payload?: unknown;
    model_snapshot?: unknown;
    /** Chat Panel attachments — prefer attachment_ids[] + downloadUrl from upload API */
    attachment_ids?: string[];
    attachments?: Array<{
      id: string;
      name: string;
      mimeType: string;
      size: number;
      downloadUrl?: string;
      dataUrl?: string;
    }>;
    message_kind?: string;
    extraction_status?: string;
    attachment_id?: string;
    artifact_item_ref?: string;
    delivery_mode?: string;
    chat_panel_recipients?: Array<{ kind: string; id: string; label?: string }>;
  };
  chips?: React.ReactNode[] | Record<string, any>;
  isTyping?: boolean;
}

/** Krybot `GET .../history` rows → in-app messages (includes merged metadata for prospector snapshot). */
function krybotHistoryMessagesToChatMessages(messages: ChatMessageResponse[]): ChatMessage[] {
  return messages.map((msg: ChatMessageResponse) => ({
    id: msg.id,
    threadId: msg.thread_id,
    who: msg.role === 'user' ? 'user' : msg.role === 'assistant' ? 'assistant' : 'system',
    text: parseMessageContent(msg.content),
    timestamp: msg.timestamp,
    type: 'text' as const,
    metadata: mergeKrybotMessageMetadata(msg) as ChatMessage['metadata'],
  }));
}

export interface ChatThread {
  id: string;
  name: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
  messageCount: number;
  isPinned: boolean;
  tags: string[];
  participants: string[]; // User IDs
  lastMessage?: ChatMessage;
  lastMessageId?: string;
  // Thread-specific model and UI role
  model: ModelSelection;
  uiRole: string;
  userId?: string; // User ID for database storage
  active_agent_id?: string | null; // Active Krybot agent ID
  /** KBO hygiene markers (GET /krybot/chat/threads) */
  isEmpty?: boolean;
  isDefaultNamed?: boolean;
  unreadCount?: number;
  isOwner?: boolean;
  context?: {
    selectedArtifactId?: string;
    selectedTaskId?: string;
    environment?: string;
    isDemo?: boolean;
    userId?: string;
    companyId?: string;
    artifactTreeId?: string;
    [key: string]: any; // Allow additional properties for flexibility
  };
}

export interface ChatState {
  threads: ChatThread[];
  currentThreadId: string | null;
  messages: ChatMessage[];
  isLoading: boolean;
  isLoadingMessages: boolean;
  isTyping: boolean;
  error: string | null;
  unreadCount: number;
}

interface ChatContextType extends ChatState {
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  // Thread Management
  createThread: (name: string, description?: string, model?: ModelSelection, uiRole?: string, context?: ChatThread['context']) => Promise<ChatThread>;
  selectThread: (threadId: string, options?: { uiRole?: string }) => void;
  deleteThread: (threadId: string) => Promise<void>;
  updateThread: (threadId: string, updates: Partial<ChatThread>) => Promise<void>;
  pinThread: (threadId: string) => Promise<void>;
  unpinThread: (threadId: string) => Promise<void>;
  loadUserThreads: () => Promise<void>;
  
  // Message Management
  loadMessages: (threadId: string, clearExisting?: boolean) => Promise<void>;
  sendMessage: (
    text: string,
    typeOrOptions?: ChatMessage['type'] | {
      type?: ChatMessage['type'];
      metadata?: Record<string, unknown>;
      threadId?: string;
    }
  ) => Promise<void>;
  addMessage: (message: Omit<ChatMessage, 'id' | 'timestamp'> & { id?: string }) => ChatMessage;
  clearMessages: () => void;
  retryMessage: (messageId: string) => Promise<void>;
  
  // Context Management
  updateThreadContext: (context: Partial<ChatThread['context']>) => void;
  getCurrentContext: () => ChatThread['context'] | null;
  
  // Search and Filter
  searchMessages: (query: string) => ChatMessage[];
  searchThreads: (query: string) => ChatThread[];
  
  // Real-time Updates
  markAsRead: (threadId: string) => void;
  subscribeToUpdates: () => void;
  unsubscribeFromUpdates: () => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { dataSource, selectedCompanyModel } = useModelSelector();
  const { artifacts } = useDataConnection();
  const { user, isAuthenticated, selectedCompany, availableCompanies } = useUser();
  const { model: chatUIModel, uiRole: chatUIRole } = useChatUI();

  const generateUUID = (): string => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };
  
  const [threads, setThreads] = useState<ChatThread[]>([]);
  const [currentThreadId, setCurrentThreadId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  
  const threadManager = useRef<ThreadManager>(ThreadManager.getInstance());
  
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastDuplicateWarningRef = useRef<{ messageId: string; timestamp: number }>({ messageId: '', timestamp: 0 });
  
  // Track if we're already loading threads to prevent duplicate calls
  const loadingThreadsRef = useRef(false);
  const loadedForUserRef = useRef<string | null>(null);
  const currentThreadIdRef = useRef<string | null>(null);
  const threadsRef = useRef<ChatThread[]>([]);
  const uiRoleOperatorSyncRef = useRef<Set<string>>(new Set());
  const selectThreadInFlightRef = useRef<string | null>(null);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);

  useEffect(() => {
    currentThreadIdRef.current = currentThreadId;
  }, [currentThreadId]);

  useEffect(() => {
    threadsRef.current = threads;
  }, [threads]);

  // Scope key: user + selected company (header switcher drives chat history)
  const threadsScopeKey = useMemo(() => {
    if (!user?.id) return null;
    return `${user.id}::${selectedCompany?.id || ''}`;
  }, [user?.id, selectedCompany?.id]);

  // Load threads when user or selected company changes
  useEffect(() => {
    if (loadingThreadsRef.current) {
      return;
    }

    if (loadedForUserRef.current === threadsScopeKey && threadsScopeKey) {
      return;
    }

    if (isAuthenticated && user) {
      // Clear prior company thread so we do not preserve Bob's chat under IEEE (etc.)
      setCurrentThreadId(null);
      setMessages([]);
      loadingThreadsRef.current = true;
      loadUserThreads().finally(() => {
        loadingThreadsRef.current = false;
        loadedForUserRef.current = threadsScopeKey;
      });
    } else {
      setThreads([]);
      setCurrentThreadId(null);
      setMessages([]);
      loadedForUserRef.current = null;
    }
  }, [threadsScopeKey, isAuthenticated, dataSource]);

  // Track the last ChatUI model to detect changes
  const lastChatUIModelRef = useRef<string | null>(null);

  // Reload threads when ChatUI model changes
  useEffect(() => {
    if (isAuthenticated && user && chatUIModel) {
      const modelKey = `${chatUIModel.type}-${chatUIModel.artifactId || ''}-${chatUIModel.artifactTreeId || ''}`;

      if (lastChatUIModelRef.current === modelKey) {
        return;
      }

      lastChatUIModelRef.current = modelKey;

      if (process.env.NODE_ENV === 'development') {
        console.log('🔄 [ChatContext] ChatUI model changed, reloading threads:', {
          modelType: chatUIModel.type,
          artifactId: chatUIModel.artifactId,
          artifactTreeId: chatUIModel.artifactTreeId,
        });
      }

      loadedForUserRef.current = null;

      if (!loadingThreadsRef.current) {
        loadingThreadsRef.current = true;
        loadUserThreads().finally(() => {
          loadingThreadsRef.current = false;
          loadedForUserRef.current = threadsScopeKey;
        });
      }
    } else if (!chatUIModel) {
      lastChatUIModelRef.current = null;
    }
  }, [
    chatUIModel?.type,
    chatUIModel?.artifactId,
    chatUIModel?.artifactTreeId,
    isAuthenticated,
    user?.id,
    threadsScopeKey,
  ]);

  const loadUserThreads = async () => {
    if (!user) return;
    
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 [ChatContext.loadUserThreads] Starting with:', {
        dataSource,
        userId: user.id,
        userName: user.username
      });
    }
    
    if (dataSource === 'sample') {
      // For sample mode, use sample threads
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 [ChatContext.loadUserThreads] Using sample mode');
      }
      await initializeSampleChat();
      return;
    }
    
    try {
      const activeCompanyId = selectedCompany?.id;

      // Prefer Krybot list (hygiene markers); fall back to legacy ThreadService.
      let userThreads: ChatThread[] = [];
      try {
        const krybotRows = await KrybotService.listChatThreads(user.id, {
          companyId: activeCompanyId || undefined,
        });
        userThreads = krybotRows.map(
          (row) => chatThreadFromBackend(row as unknown as Record<string, unknown>) as ChatThread
        );
      } catch (krybotListErr) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('⚠️ [ChatContext.loadUserThreads] Krybot list failed, using ThreadService:', krybotListErr);
        }
        userThreads = await ThreadService.getByUser(user.id);
      }

      // Always filter client-side so company switch cannot leave another company's thread selected.
      userThreads = filterThreadsByCompanyId(userThreads, activeCompanyId);
      setUnreadCount(
        userThreads.reduce((sum, t) => sum + (typeof t.unreadCount === 'number' ? t.unreadCount : 0), 0)
      );

      if (process.env.NODE_ENV === 'development') {
        console.log('📋 [ChatContext.loadUserThreads] Threads returned from backend:', {
          count: userThreads.length,
          companyId: activeCompanyId,
          threadIds: userThreads.map(t => ({
            id: t.id,
            name: t.name,
            updatedAt: t.updatedAt,
            isEmpty: t.isEmpty,
            isDefaultNamed: t.isDefaultNamed,
            messageCount: t.messageCount,
            companyId: t.context?.companyId,
          }))
        });
      }

      // Prefer reclaiming an empty default-named draft over minting another bootstrap.
      const reusableDraft = findReusableEmptyDraft(userThreads);
      if (reusableDraft && !currentThreadIdRef.current) {
        if (process.env.NODE_ENV === 'development') {
          console.log('♻️ [ChatContext.loadUserThreads] Selecting reusable empty draft:', {
            threadId: reusableDraft.id,
            name: reusableDraft.name,
          });
        }
        setThreads(userThreads);
        setCurrentThreadId(reusableDraft.id);
        return;
      }
      
      if (userThreads.length > 0) {
        // Filter threads by ChatUI model context
        let filteredThreads = userThreads;
        if (chatUIModel) {
          if (process.env.NODE_ENV === 'development') {
            console.log('🔍 [ChatContext.loadUserThreads] Filtering threads by ChatUI model:', {
              chatUIModel: JSON.stringify(chatUIModel, null, 2),
              totalThreads: userThreads.length,
              threadModels: userThreads.map(t => ({
                id: t.id,
                name: t.name,
                model: t.model ? JSON.stringify(t.model, null, 2) : 'NO MODEL'
              }))
            });
          }
          
          filteredThreads = userThreads.filter(thread => {
            if (!thread.model) {
              // Only log sample of threads without models to reduce noise
              if (process.env.NODE_ENV === 'development' && Math.random() < 0.1) {
                console.log('❌ [ChatContext.loadUserThreads] Thread has no model (sample):', {
                  threadId: thread.id,
                  threadName: thread.name
                });
              }
              return false;
            }
            
            // Check if thread model matches ChatUI model
            let matches = false;
            if (chatUIModel.type === ModelType.PRIME) {
              matches = thread.model.type === ModelType.PRIME && 
                       thread.model.artifactId === chatUIModel.artifactId;
            } else if (chatUIModel.type === ModelType.COMPANY) {
              matches = thread.model.type === ModelType.COMPANY && 
                       thread.model.artifactTreeId === chatUIModel.artifactTreeId;
            }
            
            // Only log sample of thread matching to reduce noise
            if (process.env.NODE_ENV === 'development' && Math.random() < 0.1) {
              console.log(`🔍 [ChatContext.loadUserThreads] Thread ${thread.name} (${thread.id}) (sample):`, {
                matches,
                reason: !matches ? (
                  chatUIModel.type === ModelType.PRIME 
                    ? `Type mismatch or artifactId mismatch: thread=${thread.model.type}/${thread.model.artifactId}, chatUI=${chatUIModel.type}/${chatUIModel.artifactId}`
                    : `Type mismatch or artifactTreeId mismatch: thread=${thread.model.type}/${thread.model.artifactTreeId}, chatUI=${chatUIModel.type}/${chatUIModel.artifactTreeId}`
                ) : 'MATCHES'
              });
            }
            
            return matches;
          });
          
          if (process.env.NODE_ENV === 'development') {
            console.log('✅ [ChatContext.loadUserThreads] Filtered threads result:', {
              totalThreads: userThreads.length,
              filteredThreads: filteredThreads.length,
              filteredThreadIds: filteredThreads.map(t => ({ id: t.id, name: t.name })),
              chatUIModel: JSON.stringify(chatUIModel, null, 2)
            });
          }
        } else {
          if (process.env.NODE_ENV === 'development') {
            console.log('⚠️ [ChatContext.loadUserThreads] No ChatUI model context, showing all threads:', {
              totalThreads: userThreads.length
            });
          }
        }
        
        const preservedThreadId = currentThreadIdRef.current;
        const preservedThread =
          preservedThreadId != null
            ? userThreads.find((t) => t.id === preservedThreadId)
            : undefined;

        const displayThreads =
          filteredThreads.length > 0 || !chatUIModel ? filteredThreads : userThreads;

        const threadsForState =
          preservedThread && !displayThreads.some((t) => t.id === preservedThreadId)
            ? [preservedThread, ...displayThreads]
            : displayThreads;
        setThreads(threadsForState);

        if (userThreads.length === 0 && chatUIModel && !preservedThread) {
          if (process.env.NODE_ENV === 'development') {
            console.log('📝 [ChatContext.loadUserThreads] No threads found for ChatUI model context, creating default thread');
          }
          
          const defaultThread = await createThread(
            `${user.fullName || user.username}'s ${chatUIModel.type === ModelType.PRIME ? 'Prime' : 'Company'} Chat`,
            'Main conversation thread',
            chatUIModel,
            chatUIRole || 'operator'
          );
          setCurrentThreadId(defaultThread.id);
          setMessages([]);
          return;
        }

        if (preservedThread) {
          if (process.env.NODE_ENV === 'development') {
            console.log('🔒 [ChatContext.loadUserThreads] Preserving selected thread:', {
              threadId: preservedThreadId,
              threadName: preservedThread.name,
            });
          }
          setCurrentThreadId(preservedThreadId);
          return;
        }

        const activeId = currentThreadIdRef.current;
        if (activeId && userThreads.some((t) => t.id === activeId)) {
          if (process.env.NODE_ENV === 'development') {
            console.log('🔒 [ChatContext.loadUserThreads] Keeping active thread after reload:', {
              threadId: activeId,
            });
          }
          setCurrentThreadId(activeId);
          return;
        }
        
        const sortedThreads = threadsForState.sort((a, b) => 
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        );
        
        const mostRecentThread = sortedThreads[0];
        
        if (process.env.NODE_ENV === 'development') {
          console.log('🎯 [ChatContext.loadUserThreads] Selected most recent thread:', {
            threadId: mostRecentThread.id,
            threadName: mostRecentThread.name,
            updatedAt: mostRecentThread.updatedAt
          });
        }
        
        setCurrentThreadId(mostRecentThread.id);
        await loadMessages(mostRecentThread.id);
      } else {
        // Create a default thread for the user
        // Use ChatUI model context if available, otherwise fall back to model selector
        const defaultModel = chatUIModel || { 
          type: ModelType.COMPANY,
          artifactTreeId: selectedCompanyModel?.artifactTreeId
        };
        const defaultThread = await createThread(
          `${user.fullName || user.username}'s General Chat`,
          'Main conversation thread',
          defaultModel,
          'operator' // Default to operator role as per ChatUI defaults
        );
        setCurrentThreadId(defaultThread.id);
        setMessages([]);
      }
    } catch (error) {
      const errorStatus = (error as any)?.response?.status;
      const isAuthError = errorStatus === 401 || errorStatus === 403;
      const isCorsError = (error as any)?.code === 'ERR_NETWORK' || 
                         (error as any)?.message?.includes('CORS');
      
      // Suppress verbose logging for expected auth/CORS errors
      if (!isAuthError && !isCorsError) {
        console.error('❌ [ChatContext.loadUserThreads] FAILED to load user threads:', error);
        if (process.env.NODE_ENV === 'development') {
          console.error('❌ [ChatContext.loadUserThreads] Error details:', {
            name: error instanceof Error ? error.name : 'Unknown',
            message: error instanceof Error ? error.message : String(error),
            status: errorStatus
          });
        }
      }
      
      // Don't fall back to sample data in live mode - just show empty threads
      // Auth errors are handled by API client, so we just show empty state
      setThreads([]);
      setCurrentThreadId(null);
      setMessages([]);
    }
  };

  const initializeSampleChat = async () => {
    try {
      // Get sample threads from service
      const sampleThreads = await sampleChatService.getSampleThreads();
      
      // Get messages for the first thread
      const firstThread = sampleThreads[0];
      const sampleMessages = await sampleChatService.getSampleMessages(firstThread.id, artifacts);

      setThreads(sampleThreads);
      setCurrentThreadId(firstThread.id);
      setMessages(sampleMessages);
      
      console.log('Sample chat initialized:', {
        threads: sampleThreads.length,
        messages: sampleMessages.length,
        artifacts: artifacts.length
      });
    } catch (error) {
      console.error('Failed to initialize sample chat:', error);
      // Fallback to simple sample thread
      await createDefaultThread();
    }
  };

  const createDefaultThread = async () => {
    const defaultThread: ChatThread = {
      id: 'default',
      name: 'General Chat',
      description: 'Main conversation thread',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      messageCount: 0,
      isPinned: true,
      tags: ['general'],
      participants: [],
      model: { 
        type: ModelType.COMPANY,
        artifactTreeId: selectedCompanyModel?.artifactTreeId
      },
      uiRole: 'architect',
    };
    
    setThreads([defaultThread]);
    setCurrentThreadId('default');
    setMessages([]);
  };

  const loadMessages = useCallback(async (threadId: string, clearExisting: boolean = false) => {
    if (dataSource === 'sample') {
      // For sample mode, don't try to load from API
      return;
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('📥 [ChatContext.loadMessages] Loading messages for thread:', threadId);
    }

    setIsLoadingMessages(true);
    try {
      // Krybot-first so prospector (and other Krybot threads) get full `metadata` / snapshot fields.
      // ThreadService is legacy fallback when history is empty or Krybot is unavailable.
      if (dataSource === 'live') {
        try {
          const historyResponse = await KrybotService.getChatHistory(threadId, {
            userId: user?.id,
          });
          if (historyResponse.messages && historyResponse.messages.length > 0) {
            const transformedMessages = krybotHistoryMessagesToChatMessages(historyResponse.messages);
            setMessages((prevMessages) => {
              if (clearExisting) {
                return transformedMessages;
              }
              const existingIds = new Set(prevMessages.map((m) => m.id));
              const newMessages = transformedMessages.filter((msg) => !existingIds.has(msg.id));
              return newMessages.length > 0 ? [...prevMessages, ...newMessages] : prevMessages;
            });
            return;
          }
        } catch (krybotErr: unknown) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('⚠️ [ChatContext.loadMessages] Krybot getChatHistory failed, falling back to ThreadService:', krybotErr);
          }
        }
      }

      // Use ThreadService.getMessages which handles response aliases
      const backendMessages = await ThreadService.getMessages(threadId, { limit: 100 });
      
      if (process.env.NODE_ENV === 'development') {
        console.log('✅ [ChatContext.loadMessages] Messages loaded:', {
          threadId,
          messageCount: backendMessages.length,
          messages: backendMessages
        });
      }
      
      // Transform backend messages to frontend format
      // ThreadService.getMessages already handles basic transformation, but we need to handle metadata
      const transformedMessages = backendMessages
        .filter((msg: any) => {
          // Skip old card messages that might have incompatible metadata
          // Keep only non-card messages or card messages with valid metadata
          if (msg.type === 'card' && msg.message_metadata) {
            const metadata = msg.message_metadata;
            // Check if card has proper structure
            // Artifact cards need cardType and artifact
            // Process-flow cards need cardType and processTypes
            if (!metadata.cardType) {
              console.warn('⚠️ [ChatContext] Skipping card message without cardType:', msg.id);
              return false; // Skip this message
            }
            // Validate based on card type
            if (metadata.cardType === 'artifact' && !metadata.artifact) {
              console.warn('⚠️ [ChatContext] Skipping artifact card without artifact data:', msg.id);
              return false;
            }
            if ((metadata.cardType === 'process-flow' || metadata.cardType === 'flow-diagram') && !metadata.processTypes) {
              console.warn('⚠️ [ChatContext] Skipping process-flow card without processTypes:', msg.id);
              return false;
            }
          }
          return true; // Keep this message
        })
        .map((msg: ChatMessage) => ({
          ...msg,
          // Ensure metadata is properly set
          metadata: msg.metadata || {},
        }));
      
      // Intelligently merge messages instead of replacing (unless clearExisting is true)
      setMessages(prevMessages => {
        if (clearExisting) {
          // When switching threads, replace all messages
          return transformedMessages;
        }
        
        // Only add messages that don't already exist (by ID)
        const existingIds = new Set(prevMessages.map(m => m.id));
        const newMessages = transformedMessages.filter((msg: any) => !existingIds.has(msg.id));
        
        if (newMessages.length > 0) {
          return [...prevMessages, ...newMessages];
        }
        
        // No new messages, return existing array to avoid re-render
        return prevMessages;
      });
    } catch (err: any) {
      // Suppress CORS/network errors - these are expected backend configuration issues
      const isCorsError = err.code === 'ERR_NETWORK' || 
                         err.message?.includes('CORS') ||
                         err.message?.includes('Access-Control-Allow-Origin');
      const isAuthError = err.response?.status === 401 || err.response?.status === 403;
      
      if (process.env.NODE_ENV === 'development') {
        console.error('❌ [ChatContext.loadMessages] Failed to load messages:', {
          threadId,
          error: err.message,
          status: err.response?.status,
          isCorsError,
          isAuthError,
          fullError: err
        });
      }
      
      if (!isCorsError && !isAuthError && err.response?.status !== 404) {
        console.error('Failed to load messages:', err);
      }
      
      if (err.response?.status === 404) {
        setMessages([]);
      } else {
        setError('Failed to load messages');
        setMessages([]);
      }
    } finally {
      setIsLoadingMessages(false);
    }
  }, [dataSource, user?.id]);

  const createThread = useCallback(async (
    name: string, 
    description?: string, 
    model?: ModelSelection, 
    uiRole?: string, 
    context?: ChatThread['context']
  ): Promise<ChatThread> => {
    // Use ChatUI context as defaults if not provided
    const defaultModel = model || chatUIModel || { type: ModelType.COMPANY };
    const defaultUIRole = uiRole || chatUIRole || 'operator';
    if (dataSource === 'sample') {
      // For sample mode, use local thread manager
      try {
        const newThread = threadManager.current.createThread(name, description, undefined, defaultModel, defaultUIRole, context);
        setThreads(prev => [...prev, newThread]);
        return newThread;
      } catch (err) {
        // Fallback to local creation
        const newThread: ChatThread = {
          id: generateUUID(),
          name,
          description,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          messageCount: 0,
          isPinned: false,
          tags: [],
          participants: [],
          model: defaultModel,
          uiRole: defaultUIRole,
          context,
        };
        
        setThreads(prev => [...prev, newThread]);
        return newThread;
      }
    } else {
      // For live mode, bootstrap with Krybot
      try {
        if (!user) {
          throw new Error('User must be selected to create threads');
        }
        
        // Prepare artifact context with demo flag and model information
        const artifactContext: Record<string, any> = { ...(context || {}) };
        if (context?.isDemo) {
          artifactContext.is_demo = true;
        }
        
        // Include model information in artifact_context so backend can save it
        if (model) {
          if (model.type === ModelType.COMPANY && model.artifactTreeId) {
            artifactContext.artifact_tree_id = model.artifactTreeId;
          }
          if (model.type === ModelType.COMPANY && model.businessModelId) {
            artifactContext.business_model_id = model.businessModelId;
          }
          if (model.type === ModelType.PRIME && model.artifactId) {
            artifactContext.prime_artifact_id = model.artifactId;
          }
          artifactContext.model_type = model.type;
        }
        
        // Log what we're sending to backend
        if (process.env.NODE_ENV === 'development') {
          console.log('🔍 [ChatContext.createThread] Creating thread with model:', {
            model,
            artifactContext,
            metadataModel: model || { type: ModelType.COMPANY }
          });
        }
        
        // Bootstrap thread with Krybot
        const companyIdForCtx =
          (context as { companyId?: string } | undefined)?.companyId ||
          selectedCompany?.id ||
          user.companyId;
        const artifactTreeForCtx =
          (context as { artifactTreeId?: string } | undefined)?.artifactTreeId ||
          (defaultModel?.type === ModelType.COMPANY ? defaultModel.artifactTreeId : undefined);

        let companyNameForKryspace: string | undefined;
        if (companyIdForCtx) {
          if (selectedCompany?.id === companyIdForCtx && selectedCompany.name) {
            companyNameForKryspace = selectedCompany.name;
          } else {
            const row = availableCompanies.find((uc) => uc.company.id === companyIdForCtx);
            companyNameForKryspace = row?.company.name;
          }
        }

        const ctxExtra = context as
          | { claude_project_ref?: string; claudeProjectRef?: string }
          | undefined;
        const claudeProjectRefForBootstrap =
          ctxExtra?.claude_project_ref?.trim() || ctxExtra?.claudeProjectRef?.trim() || undefined;

        const bootstrapResponse = await KrybotService.bootstrapThread({
          user_id: user.id,
          ui_role: defaultUIRole,
          artifact_context: artifactContext,
          context: {
            company_id: companyIdForCtx,
            artifact_tree_id: artifactTreeForCtx,
          },
          metadata: {
            model: defaultModel,
            name,
            description,
            ...(context?.isDemo ? { is_demo: true } : {}),
            ...(defaultUIRole === 'prospector' && companyIdForCtx
              ? buildProspectingBootstrapMetadata({
                  companyId: companyIdForCtx,
                  companyDisplayName: companyNameForKryspace,
                  claudeProjectRef: claudeProjectRefForBootstrap,
                })
              : {}),
          },
        });
        
        // Convert backend thread to frontend format
        const threadReused = Boolean(bootstrapResponse.thread_reused);
        const frontendThread = chatThreadFromBackend(bootstrapResponse.thread) as ChatThread;
        frontendThread.name = resolveBootstrapThreadName(
          frontendThread.name,
          name,
          threadReused
        );
        if (description && !threadReused) frontendThread.description = description;
        if (context) {
          frontendThread.context = { ...(frontendThread.context || {}), ...context };
        }
        // Always keep the role we asked for (backend metadata can omit ui_role)
        frontendThread.uiRole = defaultUIRole;
        if (threadReused) {
          frontendThread.isEmpty = frontendThread.isEmpty ?? true;
          frontendThread.isDefaultNamed =
            frontendThread.isDefaultNamed ??
            frontendThread.name === 'Krybot Chat';
        }
        
        // Ensure model information is preserved (backend might not return it properly)
        if (model && (!frontendThread.model || !frontendThread.model.artifactTreeId)) {
          frontendThread.model = model;
          if (process.env.NODE_ENV === 'development') {
            console.log('🔍 [ChatContext.createThread] Restoring model info on frontend thread:', model);
          }
        }
        
        // Log thread creation for debugging
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ [ChatContext.createThread] Thread created successfully:', {
            threadId: frontendThread.id,
            threadName: frontendThread.name,
            threadReused,
            userId: user.id,
            model: frontendThread.model,
            artifactContext: artifactContext
          });
        }
        
        // Add starter messages if any
        if (bootstrapResponse.starter_messages && bootstrapResponse.starter_messages.length > 0) {
          const starterChatMessages: ChatMessage[] = krybotHistoryMessagesToChatMessages(
            bootstrapResponse.starter_messages
          );
          setMessages(starterChatMessages);
        }
        
        setThreads(prev => {
          const updated = upsertThreadById(prev, frontendThread);
          // Sync ref immediately so selectThread right after createThread keeps uiRole
          threadsRef.current = updated;
          if (process.env.NODE_ENV === 'development') {
            console.log('📝 [ChatContext.createThread] Upserted thread in local state. Total threads:', updated.length, 
              'Thread IDs:', updated.map(t => ({ id: t.id, name: t.name })),
              'reused:', threadReused);
          }
          return updated;
        });

        if (defaultUIRole === 'prospector' && !bootstrapResponse.thread.active_agent_id) {
          try {
            await KrybotService.ensureChatAgentAssigned(
              frontendThread.id,
              bootstrapResponse.thread,
              user.id,
              defaultUIRole
            );
          } catch (agentError) {
            if (process.env.NODE_ENV === 'development') {
              console.warn('⚠️ [ChatContext.createThread] Failed to assign prospector agent:', agentError);
            }
          }
        }

        return frontendThread;
      } catch (err: any) {
        const isExpectedError =
          err.code === 'ERR_NETWORK' ||
          err.message?.includes('CORS') ||
          err.message?.includes('Access-Control-Allow-Origin') ||
          err.response?.status === 500;
        if (!isExpectedError) {
          console.error('Failed to create thread with Krybot:', err);
        }
        const detail = err?.response?.data?.detail;
        const msg =
          typeof detail === 'string'
            ? detail
            : err?.message || 'Failed to create chat thread';
        setError(msg);
        throw err instanceof Error ? err : new Error(String(msg));
      }
    }
  }, [dataSource, user, chatUIModel, chatUIRole, selectedCompany, availableCompanies]);

  const updateThread = useCallback(async (threadId: string, updates: Partial<ChatThread>) => {
    if (dataSource === 'sample') {
      // For sample mode, just update local state
      setThreads(prev => prev.map(t => 
        t.id === threadId 
          ? { ...t, ...updates, updatedAt: new Date().toISOString() }
          : t
      ));
      return;
    }

    try {
      // Filter updates to only include fields allowed by ChatThreadUpdate schema
      // Allowed fields: name, description, user_id, is_pinned, tags, participants, context, model, ui_role, message_count, last_message_id
      const allowedFields: (keyof ChatThread)[] = ['name', 'description', 'userId', 'isPinned', 'tags', 'participants', 'context', 'model', 'uiRole', 'messageCount', 'lastMessageId'];
      const filteredUpdates: Record<string, any> = {};
      for (const key of allowedFields) {
        if (key in updates) {
          const value = updates[key];
          if (value !== undefined) {
            filteredUpdates[key] = value;
          }
        }
      }
      
      const backendUpdates = chatThreadToBackend(filteredUpdates);
      console.log('🔄 [ChatContext.updateThread] Updating thread:', {
        threadId,
        updates: filteredUpdates,
        backendUpdates
      });
      const response = await api.patch(`/chat/threads/${threadId}`, backendUpdates);
      console.log('📥 [ChatContext.updateThread] Backend response:', {
        rawResponse: response.data,
        isPinned: response.data?.is_pinned,
        isPinnedField: response.data?.isPinned,
        allKeys: Object.keys(response.data || {}),
        fullResponse: JSON.stringify(response.data, null, 2)
      });
      const frontendThread = chatThreadFromBackend(response.data) as ChatThread;
      const mergedThread: ChatThread = {
        ...frontendThread,
        ...filteredUpdates,
        updatedAt: frontendThread.updatedAt ?? new Date().toISOString(),
      };
      console.log('✅ [ChatContext.updateThread] Thread updated successfully:', {
        threadId,
        updatedThread: mergedThread,
        isPinned: mergedThread.isPinned,
        allFields: Object.keys(mergedThread)
      });
      
      // Update threads state
      setThreads(prev => {
        const updated = prev.map(t => {
          if (t.id === threadId) {
            console.log('🔄 [ChatContext.updateThread] Replacing thread in state:', {
              oldThread: { id: t.id, isPinned: t.isPinned },
              newThread: { id: mergedThread.id, isPinned: mergedThread.isPinned }
            });
            return mergedThread;
          }
          return t;
        });
        console.log('📋 [ChatContext.updateThread] Updated threads state:', {
          totalThreads: updated.length,
          pinnedThreads: updated.filter(t => t.isPinned).length,
          updatedThread: updated.find(t => t.id === threadId)
        });
        return updated;
      });
    } catch (err) {
      console.error('❌ [ChatContext.updateThread] Failed to update thread:', err);
      // Fallback to local update
      setThreads(prev => prev.map(t => 
        t.id === threadId 
          ? { ...t, ...updates, updatedAt: new Date().toISOString() }
          : t
      ));
    }
  }, [dataSource]);

  const selectThread = useCallback(async (
    threadId: string,
    options?: { uiRole?: string },
  ) => {
    if (selectThreadInFlightRef.current === threadId) {
      return;
    }
    selectThreadInFlightRef.current = threadId;

    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 [ChatContext.selectThread] Starting thread selection:', {
        threadId,
        dataSource,
        currentThreadId
      });
    }
    
    try {
    // Clear current messages first to show loading state
    setMessages([]);
    setCurrentThreadId(threadId);
    currentThreadIdRef.current = threadId;
    
    if (dataSource === 'sample') {
      if (process.env.NODE_ENV === 'development') {
        console.log('🔍 [ChatContext.selectThread] Loading sample messages');
      }
      // Load sample messages for the selected thread
      try {
        const sampleMessages = await sampleChatService.getSampleMessages(threadId, artifacts);
        setMessages(sampleMessages);
      } catch (error) {
        setMessages([]);
      }
    } else {
      
      // Bootstrap thread with Krybot if user is available
      if (user) {
        try {
          const currentThread = threadsRef.current.find(t => t.id === threadId);
          const ctxAny = currentThread?.context as Record<string, unknown> | undefined;
          const agentModeFromContext =
            typeof ctxAny?.agent_mode === 'string'
              ? ctxAny.agent_mode
              : typeof ctxAny?.agentMode === 'string'
                ? ctxAny.agentMode
                : undefined;
          // Explicit option wins — prospector page must not fall back to ChatUI operator
          const uiForBootstrap =
            options?.uiRole ||
            currentThread?.uiRole ||
            (agentModeFromContext === 'prospector' ? 'prospector' : undefined) ||
            chatUIRole ||
            'operator';
          const companyIdForBootstrap =
            (typeof ctxAny?.companyId === 'string' && ctxAny.companyId) ||
            selectedCompany?.id ||
            user.companyId;
          const artifactTreeForBootstrap =
            (currentThread?.model?.type === ModelType.COMPANY && currentThread.model.artifactTreeId) ||
            (typeof ctxAny?.artifactTreeId === 'string' && ctxAny.artifactTreeId) ||
            undefined;
          const artifactContext: Record<string, unknown> = {
            ...(currentThread?.context && typeof currentThread.context === 'object'
              ? (currentThread.context as Record<string, unknown>)
              : {}),
          };
          if (companyIdForBootstrap) {
            artifactContext.company_id = companyIdForBootstrap;
          }
          if (artifactTreeForBootstrap) {
            artifactContext.artifact_tree_id = artifactTreeForBootstrap;
          }
          const bootstrapResponse = await KrybotService.bootstrapThread({
            user_id: user.id,
            thread_id: threadId,
            ui_role: uiForBootstrap,
            artifact_context: artifactContext,
            context: {
              company_id: companyIdForBootstrap,
              artifact_tree_id: artifactTreeForBootstrap,
            },
            metadata: {
              model: currentThread?.model,
              ...(uiForBootstrap === 'prospector' && companyIdForBootstrap
                ? buildProspectingBootstrapMetadata({
                    companyId: companyIdForBootstrap,
                    companyDisplayName:
                      selectedCompany?.id === companyIdForBootstrap
                        ? selectedCompany.name
                        : availableCompanies.find((uc) => uc.company.id === companyIdForBootstrap)
                            ?.company.name,
                  })
                : {}),
            },
          });
          
          // Ensure chat agent is assigned (frontend-side check for production backend)
          if (!bootstrapResponse.thread.active_agent_id) {
            console.log('🔍 [ChatContext.selectThread] Thread has no agent, assigning chat agent...');
            try {
              await KrybotService.ensureChatAgentAssigned(
                threadId,
                bootstrapResponse.thread,
                user.id,
                uiForBootstrap
              );
              // Reload thread to get updated agent_id
              const updatedBootstrap = await KrybotService.getChatHistory(threadId, {
                userId: user?.id,
              });
              if (updatedBootstrap.thread.active_agent_id) {
                console.log('✅ [ChatContext.selectThread] Chat agent assigned:', updatedBootstrap.thread.active_agent_id);
                // Update bootstrap response with updated thread
                bootstrapResponse.thread = updatedBootstrap.thread;
              }
            } catch (agentError) {
              console.warn('⚠️ [ChatContext.selectThread] Failed to assign chat agent:', agentError);
              // Continue anyway - backend should handle it
            }
          }
          
          try {
            const historyResponse = await KrybotService.getChatHistory(threadId, {
              userId: user?.id,
            });
            if (historyResponse.messages && historyResponse.messages.length > 0) {
              if (process.env.NODE_ENV === 'development') {
                console.log('✅ [ChatContext.selectThread] Loaded messages from getChatHistory:', {
                  threadId,
                  messageCount: historyResponse.messages.length,
                });
              }
              const chatMessages: ChatMessage[] = krybotHistoryMessagesToChatMessages(
                historyResponse.messages
              );
              setMessages(chatMessages);
            } else if (
              bootstrapResponse.starter_messages &&
              bootstrapResponse.starter_messages.length > 0
            ) {
              if (process.env.NODE_ENV === 'development') {
                console.log('✅ [ChatContext.selectThread] No history; using bootstrap starter messages:', {
                  threadId,
                  messageCount: bootstrapResponse.starter_messages.length,
                });
              }
              const chatMessages: ChatMessage[] = krybotHistoryMessagesToChatMessages(
                bootstrapResponse.starter_messages
              );
              setMessages(chatMessages);
            } else {
              await loadMessages(threadId, true);
            }
          } catch (historyError: unknown) {
            if (process.env.NODE_ENV === 'development') {
              const err = historyError as { message?: string; response?: { status?: number } };
              console.warn('⚠️ [ChatContext.selectThread] getChatHistory failed, falling back:', {
                error: err.message,
                status: err.response?.status,
              });
            }
            if (
              bootstrapResponse.starter_messages &&
              bootstrapResponse.starter_messages.length > 0
            ) {
              const chatMessages: ChatMessage[] = krybotHistoryMessagesToChatMessages(
                bootstrapResponse.starter_messages
              );
              setMessages(chatMessages);
            } else {
              await loadMessages(threadId, true);
            }
          }
        } catch (error: any) {
          if (process.env.NODE_ENV === 'development') {
            console.error('❌ [ChatContext.selectThread] Failed to bootstrap thread with Krybot:', {
              threadId,
              error: error.message,
              status: error.response?.status,
              fullError: error
            });
          }
          // Fallback to regular message loading
          if (process.env.NODE_ENV === 'development') {
            console.log('🔄 [ChatContext.selectThread] Falling back to loadMessages');
          }
          await loadMessages(threadId, true);
        }
      } else {
        // No user, just load messages normally
        if (process.env.NODE_ENV === 'development') {
          console.log('🔄 [ChatContext.selectThread] No user, loading messages directly');
        }
        await loadMessages(threadId, true);
      }
    }
    
    if (dataSource === 'live') {
      void markAsRead(threadId);
      
      // Load command history for the thread
      if (user?.id) {
        try {
          if (process.env.NODE_ENV === 'development') {
            console.log('📜 [ChatContext.selectThread] Loading command history for thread:', threadId);
          }
          const commandHistory = await CommandHistoryService.getCommandHistoryByThread(threadId, user.id);
          if (process.env.NODE_ENV === 'development') {
            console.log('✅ [ChatContext.selectThread] Command history loaded:', {
              threadId,
              commandCount: commandHistory.length
            });
          }
          // Store command history in thread context or separate state if needed
          // For now, we'll just log it - can be extended to display in UI later
        } catch (error: any) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('⚠️ [ChatContext.selectThread] Failed to load command history:', {
              threadId,
              error: error.message
            });
          }
          // Don't fail thread selection if command history fails to load
        }
      }
    }
    
    // UX Principle: Sync thread uiRole with selected UI role to prevent layout flips
    // If the user has explicitly selected 'operator' mode, ensure the thread reflects that
    if (chatUIRole === 'operator') {
      const selectedThread = threadsRef.current.find(t => t.id === threadId);
      if (
        selectedThread &&
        selectedThread.uiRole !== 'operator' &&
        !isProtectedChatThreadUiRole(selectedThread.uiRole) &&
        !uiRoleOperatorSyncRef.current.has(threadId)
      ) {
        uiRoleOperatorSyncRef.current.add(threadId);
        if (process.env.NODE_ENV === 'development') {
          console.log('🔄 [ChatContext.selectThread] Syncing thread uiRole to operator:', {
            threadId,
            currentThreadRole: selectedThread.uiRole,
            selectedUIRole: chatUIRole
          });
        }
        try {
          await updateThread(threadId, { uiRole: 'operator' });
        } catch (error) {
          // Don't fail thread selection if sync fails
          if (process.env.NODE_ENV === 'development') {
            console.warn('⚠️ [ChatContext.selectThread] Failed to sync thread uiRole:', error);
          }
        }
      }
    }
    } finally {
      if (selectThreadInFlightRef.current === threadId) {
        selectThreadInFlightRef.current = null;
      }
    }
  }, [loadMessages, dataSource, artifacts, user, chatUIRole, updateThread, selectedCompany]);

  const deleteThread = useCallback(async (threadId: string) => {
    if (dataSource === 'sample') {
      // For sample mode, just remove from local state
      setThreads(prev => prev.filter(t => t.id !== threadId));
      
      if (currentThreadId === threadId) {
        const remainingThreads = threads.filter(t => t.id !== threadId);
        if (remainingThreads.length > 0) {
          setCurrentThreadId(remainingThreads[0].id);
          // Load sample messages for the new current thread
          try {
            const sampleMessages = await sampleChatService.getSampleMessages(remainingThreads[0].id, artifacts);
            setMessages(sampleMessages);
          } catch (error) {
            console.error('Failed to load sample messages:', error);
          }
        } else {
          setCurrentThreadId(null);
          setMessages([]);
        }
      }
      return;
    }

    try {
      await api.delete(`/chat/threads/${threadId}`);
      setThreads(prev => prev.filter(t => t.id !== threadId));
      
      if (currentThreadId === threadId) {
        const remainingThreads = threads.filter(t => t.id !== threadId);
        if (remainingThreads.length > 0) {
          setCurrentThreadId(remainingThreads[0].id);
          await loadMessages(remainingThreads[0].id);
        } else {
          setCurrentThreadId(null);
          setMessages([]);
        }
      }
    } catch (err) {
      console.error('Failed to delete thread:', err);
      setError('Failed to delete thread');
    }
  }, [currentThreadId, threads, loadMessages, dataSource, artifacts]);

  const pinThread = useCallback(async (threadId: string) => {
    console.log('📌 [ChatContext.pinThread] Pinning thread:', threadId);
    try {
      await updateThread(threadId, { isPinned: true });
      console.log('✅ [ChatContext.pinThread] Thread pinned successfully:', threadId);
    } catch (error) {
      console.error('❌ [ChatContext.pinThread] Failed to pin thread:', threadId, error);
      throw error;
    }
  }, [updateThread]);

  const unpinThread = useCallback(async (threadId: string) => {
    console.log('📌 [ChatContext.unpinThread] Unpinning thread:', threadId);
    try {
      await updateThread(threadId, { isPinned: false });
      console.log('✅ [ChatContext.unpinThread] Thread unpinned successfully:', threadId);
    } catch (error) {
      console.error('❌ [ChatContext.unpinThread] Failed to unpin thread:', threadId, error);
      throw error;
    }
  }, [updateThread]);

  const addMessage = useCallback((message: Omit<ChatMessage, 'id' | 'timestamp'> & { id?: string }): ChatMessage => {
    const newMessage: ChatMessage = {
      ...message,
      id: message.id && String(message.id).trim() ? String(message.id) : generateUUID(),
      timestamp: new Date().toISOString(),
    };
    
    setMessages(prev => {
      // Check for duplicates by ID and threadId
      const isDuplicate = prev.some(m => 
        (m.id === newMessage.id && m.id !== undefined) || 
        (m.threadId === newMessage.threadId && 
         m.who === newMessage.who && 
         m.text === newMessage.text &&
         Math.abs(new Date(m.timestamp).getTime() - new Date(newMessage.timestamp).getTime()) < 1000)
      );
      
      if (isDuplicate) {
        // Only log duplicate warnings in development and not too frequently (throttle to once per 5 seconds per message)
        if (process.env.NODE_ENV === 'development') {
          const now = Date.now();
          if (lastDuplicateWarningRef.current.messageId !== newMessage.id || 
              now - lastDuplicateWarningRef.current.timestamp > 5000) {
            console.warn('⚠️ [ChatContext.addMessage] Duplicate message detected, skipping:', {
              id: newMessage.id,
              text: newMessage.text?.substring(0, 50),
              who: newMessage.who
            });
            lastDuplicateWarningRef.current = { messageId: newMessage.id, timestamp: now };
          }
        }
        return prev;
      }
      
      // Also filter to only include messages for the current thread
      const filtered = prev.filter(m => m.threadId === newMessage.threadId);
      return [...filtered, newMessage];
    });
    return newMessage;
  }, [currentThreadId]);

  const sendMessage = useCallback(async (
    text: string,
    typeOrOptions?: ChatMessage['type'] | {
      type?: ChatMessage['type'];
      metadata?: Record<string, unknown>;
      threadId?: string;
    }
  ) => {
    if (!user) return;

    const type =
      typeof typeOrOptions === 'string' ? typeOrOptions : typeOrOptions?.type ?? 'text';
    const extraMetadata =
      typeof typeOrOptions === 'object' && typeOrOptions !== null ? typeOrOptions.metadata : undefined;
    const threadIdOverride =
      typeof typeOrOptions === 'object' && typeOrOptions !== null ? typeOrOptions.threadId : undefined;
    const effectiveThreadId =
      threadIdOverride ?? currentThreadIdRef.current ?? currentThreadId;
    const extraAttachments = extraMetadata?.attachments;
    
    // Validate input
    const trimmedText = text?.trim();
    const hasAttachments = Array.isArray(extraAttachments) && extraAttachments.length > 0;
    if ((!trimmedText || trimmedText.length === 0) && !hasAttachments) {
      console.warn('⚠️ [ChatContext.sendMessage] Attempted to send empty message');
      return;
    }
    
    // Validate UUIDs
    if (!user.id || typeof user.id !== 'string') {
      console.error('❌ [ChatContext.sendMessage] Invalid user.id:', user.id);
      return;
    }
    
    if (!effectiveThreadId || typeof effectiveThreadId !== 'string') {
      console.error('❌ [ChatContext.sendMessage] Invalid thread id:', effectiveThreadId);
      throw new Error('No active thread. Create or select a thread before sending.');
    }

    if (effectiveThreadId !== currentThreadIdRef.current) {
      currentThreadIdRef.current = effectiveThreadId;
      setCurrentThreadId(effectiveThreadId);
    }
    
    console.log('🔍 [ChatContext.sendMessage] Starting message send:', {
      text: trimmedText.substring(0, 50) + (trimmedText.length > 50 ? '...' : ''),
      type,
      threadId: effectiveThreadId,
      userId: user.id,
      dataSource
    });
    
    // Create user message object
    const userMessage: Omit<ChatMessage, 'id' | 'timestamp'> = {
      threadId: effectiveThreadId,
      who: 'user',
      text: trimmedText ?? '',
      type,
      ...(extraMetadata ? { metadata: extraMetadata as ChatMessage['metadata'] } : {}),
    };
    
    // Add user message to local state immediately for UI responsiveness
    const tempUserMessage = addMessage(userMessage);
    
    // For live mode with Krybot integration
    if (dataSource === 'live') {
      setIsTyping(true);
      try {
        // Use Krybot to process the message
        const currentThread = threadsRef.current.find(t => t.id === effectiveThreadId);
        
        // Always check backend thread state and ensure chat agent is assigned before sending message
        // This is important because local state might not reflect backend state
        console.log('🔍 [ChatContext.sendMessage] Checking thread agent assignment...', {
          threadId: effectiveThreadId,
          localThreadHasAgent: !!currentThread?.active_agent_id
        });
        
        try {
          // Get thread state from backend to check actual agent assignment
          const threadHistory = await KrybotService.getChatHistory(effectiveThreadId);
          const backendThread = threadHistory.thread;
          
          if (!backendThread.active_agent_id) {
            console.log('🔍 [ChatContext.sendMessage] Backend thread has no agent, assigning chat agent...');
            await KrybotService.ensureChatAgentAssigned(
              effectiveThreadId,
              {
                active_agent_id: backendThread.active_agent_id,
                ui_role: (backendThread as { ui_role?: string }).ui_role,
              },
              user.id,
              isProtectedChatThreadUiRole(currentThread?.uiRole)
                ? (currentThread!.uiRole as string)
                : currentThread?.uiRole || chatUIRole || 'operator'
            );
            // Reload to get updated agent_id
            const updatedHistory = await KrybotService.getChatHistory(effectiveThreadId);
            if (updatedHistory.thread.active_agent_id) {
              console.log('✅ [ChatContext.sendMessage] Chat agent assigned:', updatedHistory.thread.active_agent_id);
              // Update thread in state
              setThreads(prev => prev.map(t => 
                t.id === effectiveThreadId 
                  ? { ...t, active_agent_id: updatedHistory.thread.active_agent_id }
                  : t
              ));
            }
          } else {
            console.log('✅ [ChatContext.sendMessage] Thread already has agent:', backendThread.active_agent_id);
            // Update local state if it's missing
            if (!currentThread?.active_agent_id) {
              setThreads(prev => prev.map(t => 
                t.id === effectiveThreadId 
                  ? { ...t, active_agent_id: backendThread.active_agent_id }
                  : t
              ));
            }
          }
        } catch (agentError: any) {
          console.warn('⚠️ [ChatContext.sendMessage] Failed to check/assign chat agent:', agentError);
          // If we can't check/assign, try to continue - backend might handle it
          // But this might cause 400 errors if agent is truly missing
        }
        
        // Try Krybot endpoint first, fallback to standard endpoint
        let turnResponse: any = null;
        let useStandardEndpoint = false;
        const workspaceSlugRaw =
          (extraMetadata as { workspaceSlug?: unknown } | undefined)?.workspaceSlug ??
          (extraMetadata as { workspace_slug?: unknown } | undefined)?.workspace_slug;
        const authorizedWorkspaceSlug =
          typeof selectedCompany?.kryspace_slug === 'string'
            ? selectedCompany.kryspace_slug.trim()
            : '';
        const workspaceSlug =
          typeof workspaceSlugRaw === 'string' && workspaceSlugRaw.trim()
            ? workspaceSlugRaw.trim()
            : authorizedWorkspaceSlug || undefined;
        const toolInvocation = chatWorkspaceToolInvocation(workspaceSlug, {
          authorizedWorkspaceSlug: authorizedWorkspaceSlug || undefined,
        });
        
        try {
          // Current-turn metadata (Control Bridge workspace focus) must win over
          // sticky thread.context — otherwise switching Fred's → DDD still orients
          // on the thread's original slug.
          const krybotPayload = {
            thread_id: effectiveThreadId,
            user_id: user.id,
            message_text: trimmedText ?? '',
            message_metadata: {
              type,
              ...(currentThread?.context && typeof currentThread.context === 'object'
                ? { context: currentThread.context }
                : {}),
              ...(extraMetadata ?? {}),
              ...(workspaceSlug
                ? { workspaceSlug, workspace_slug: workspaceSlug }
                : {}),
            },
            ...(toolInvocation ? { tool_invocation: toolInvocation } : {}),
          };
          
          if (process.env.NODE_ENV === 'development') {
            console.log('📤 [ChatContext.sendMessage] Trying Krybot endpoint with payload:', {
              ...krybotPayload,
              message_text: krybotPayload.message_text.substring(0, 50) + (krybotPayload.message_text.length > 50 ? '...' : '')
            });
          }
          
          turnResponse = await KrybotService.sendMessage(effectiveThreadId, krybotPayload);
          
          if (process.env.NODE_ENV === 'development') {
            console.log('✅ [ChatContext.sendMessage] Krybot endpoint succeeded');
          }
        } catch (krybotError: any) {
          // Check if it's a network/CORS error or if Krybot is unavailable
          const isNetworkError = krybotError.code === 'ERR_NETWORK' || 
                                 krybotError.message?.includes('CORS') ||
                                 krybotError.response?.status === 503 ||
                                 krybotError.response?.status === 404;
          
          if (isNetworkError || krybotError.response?.status === 503) {
            if (process.env.NODE_ENV === 'development') {
              console.log('⚠️ [ChatContext.sendMessage] Krybot endpoint failed, falling back to standard endpoint:', {
                error: krybotError.message,
                status: krybotError.response?.status
              });
            }
            useStandardEndpoint = true;
          } else {
            // Re-throw non-network errors (e.g., 400, 401, 500)
            throw krybotError;
          }
        }
        
        // Fallback to standard endpoint if Krybot failed
        if (useStandardEndpoint || !turnResponse) {
          try {
            if (process.env.NODE_ENV === 'development') {
              console.log('📤 [ChatContext.sendMessage] Using standard endpoint');
            }
            
            // Use ThreadService.sendMessage with standard format
            const attachmentFallback =
              Array.isArray(extraAttachments) && extraAttachments.length > 0
                ? `[${extraAttachments.length} attachment(s)]`
                : '';
            const standardMessage = await ThreadService.sendMessage(effectiveThreadId, {
              user_id: user.id,
              content: trimmedText || attachmentFallback,
              type: type || 'text',
              metadata: extraMetadata,
            });
            
            // Convert standard response to Krybot-like format for consistency
            turnResponse = {
              user_message: {
                id: standardMessage.id,
                thread_id: standardMessage.threadId,
                role: 'user',
                content: standardMessage.text,
                timestamp: standardMessage.timestamp,
              },
              assistant_message: null, // Standard endpoint doesn't auto-generate assistant messages
            };
            
            if (process.env.NODE_ENV === 'development') {
              console.log('✅ [ChatContext.sendMessage] Standard endpoint succeeded');
            }
          } catch (standardError: any) {
            console.error('❌ [ChatContext.sendMessage] Standard endpoint also failed:', standardError);
            throw standardError;
          }
        }
        
        // Update user message with backend ID
        if (turnResponse.user_message) {
          setMessages(prev => prev.map(m => 
            m.id === tempUserMessage.id ? {
              ...m,
              id: turnResponse.user_message.id,
              timestamp: turnResponse.user_message.timestamp
            } : m
          ));
        }
        
        // Add assistant message if available
        if (turnResponse.assistant_message) {
          // Parse and format the message content (handles JSON responses)
          const messageText = parseMessageContent(turnResponse.assistant_message.content);
          
          const assistantMsg: ChatMessage = {
            id: turnResponse.assistant_message.id,
            threadId: effectiveThreadId,
            who: 'assistant',
            text: messageText,
            timestamp: turnResponse.assistant_message.timestamp,
            type: 'text',
            metadata: mergeKrybotMessageMetadata(
              turnResponse.assistant_message as ChatMessageResponse
            ) as ChatMessage['metadata'],
          };
          
          // Check for duplicates before adding
          setMessages(prev => {
            const isDuplicate = prev.some(m => 
              m.id === assistantMsg.id || 
              (m.threadId === assistantMsg.threadId && 
               m.who === assistantMsg.who && 
               m.text === assistantMsg.text &&
               Math.abs(new Date(m.timestamp).getTime() - new Date(assistantMsg.timestamp).getTime()) < 1000)
            );
            
            if (isDuplicate) {
              console.warn('⚠️ [ChatContext.sendMessage] Duplicate assistant message detected, skipping');
              return prev;
            }
            
            // Filter to only include messages for current thread
            const filtered = prev.filter(m => m.threadId === effectiveThreadId);
            return [...filtered, assistantMsg];
          });
        }
        
        // Update thread
        if (turnResponse.thread) {
          const frontendThread = chatThreadFromBackend(turnResponse.thread) as ChatThread;
          setThreads(prev => prev.map(t => t.id === effectiveThreadId ? frontendThread : t));
          
          // UX Principle: Sync thread uiRole with selected UI role to prevent layout flips
          // If the user has explicitly selected 'operator' mode, ensure the thread reflects that
          if (chatUIRole === 'operator' && !isProtectedChatThreadUiRole(frontendThread.uiRole)) {
            if (
              frontendThread.uiRole !== 'operator' &&
              !uiRoleOperatorSyncRef.current.has(effectiveThreadId)
            ) {
              uiRoleOperatorSyncRef.current.add(effectiveThreadId);
            if (process.env.NODE_ENV === 'development') {
              console.log('🔄 [ChatContext.sendMessage] Syncing thread uiRole to operator:', {
                threadId: effectiveThreadId,
                currentThreadRole: frontendThread.uiRole,
                selectedUIRole: chatUIRole
              });
            }
            try {
              await updateThread(effectiveThreadId, { uiRole: 'operator' });
            } catch (error) {
              // Don't fail message send if sync fails
              if (process.env.NODE_ENV === 'development') {
                console.warn('⚠️ [ChatContext.sendMessage] Failed to sync thread uiRole:', error);
              }
            }
            }
          }
        }
        
        if (chatTurnMutatedModel(turnResponse) && workspaceSlug) {
          notifyControlBridgeModelChanged(workspaceSlug);
        }

        try {
          const historyResponse = await KrybotService.getChatHistory(effectiveThreadId);
          if (historyResponse.messages && historyResponse.messages.length > 0) {
            const syncedMessages = krybotHistoryMessagesToChatMessages(historyResponse.messages);
            setMessages(syncedMessages);
          }
        } catch (historySyncErr) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('⚠️ [ChatContext.sendMessage] Post-send history sync failed:', historySyncErr);
          }
        }

        console.log('✅ [ChatContext.sendMessage] Message processed through Krybot');
      } catch (error: any) {
        // Log detailed error information
        const errorDetails = error?.response?.data || error?.message || 'Unknown error';
        const statusCode = error?.response?.status;
        const isNetworkError = error?.code === 'ERR_NETWORK' || 
                               error?.code === 'ERR_CONNECTION_REFUSED' ||
                               error?.message?.includes('Network Error') ||
                               error?.message?.includes('CONNECTION_REFUSED');
        
        // Extract detailed error message
        let detailedError = '';
        if (error?.response?.data) {
          if (typeof error.response.data === 'string') {
            detailedError = error.response.data;
          } else if (error.response.data.detail) {
            detailedError = error.response.data.detail;
          } else {
            detailedError = JSON.stringify(error.response.data);
          }
        }
        
        console.error('❌ [ChatContext.sendMessage] Failed to process message through Krybot:', {
          error,
          statusCode,
          errorDetails,
          detailedError,
          isNetworkError,
          apiUrl: API_BASE_URL,
          payload: {
            thread_id: effectiveThreadId,
            user_id: user?.id,
            message_text: trimmedText?.substring(0, 50) + (trimmedText?.length > 50 ? '...' : ''),
          },
          fullErrorResponse: error?.response?.data
        });
        
        // Extract error message from response
        let errorMessage = 'Unknown error';
        let userFriendlyMessage = '';
        
        // Try to get detailed error from backend response
        if (error?.response?.data) {
          if (typeof error.response.data === 'string') {
            errorMessage = error.response.data;
          } else if (error.response.data.detail) {
            errorMessage = error.response.data.detail;
          } else if (error.response.data.message) {
            errorMessage = error.response.data.message;
          } else {
            errorMessage = JSON.stringify(error.response.data);
          }
        } else if (error?.message) {
          errorMessage = error.message;
        }
        
        if (isNetworkError) {
          // Connection refused or network error
          userFriendlyMessage = `Cannot connect to the backend server. `;
          
          if (API_BASE_URL.includes('104.225.141.11')) {
            userFriendlyMessage += `The production server at ${API_BASE_URL} is not reachable. `;
            userFriendlyMessage += `Please ensure the server is running, or switch to local development by setting REACT_APP_API_URL=http://localhost:8000/api/v1`;
          } else if (API_BASE_URL.includes('localhost')) {
            userFriendlyMessage += `The local backend server at ${API_BASE_URL} is not running. `;
            userFriendlyMessage += `Please start the backend server (from the krysalis_backend repository) on port 8000.`;
          } else {
            userFriendlyMessage += `The backend server at ${API_BASE_URL} is not reachable. `;
            userFriendlyMessage += `Please check your network connection and ensure the server is running.`;
          }
          
          errorMessage = userFriendlyMessage;
        } else if (error?.response?.data?.detail) {
          errorMessage = error.response.data.detail;
          userFriendlyMessage = errorMessage;
        } else if (error?.message) {
          errorMessage = error.message;
          userFriendlyMessage = errorMessage;
        } else if (typeof errorDetails === 'string') {
          errorMessage = errorDetails;
          userFriendlyMessage = errorDetails;
        } else if (typeof errorDetails === 'object') {
          errorMessage = JSON.stringify(errorDetails);
          userFriendlyMessage = 'An error occurred processing your message. Please check the console for details.';
        }
        
        // Fallback: add error message
        const errorChatMessage: ChatMessage = {
          id: generateUUID(),
          threadId: effectiveThreadId,
          who: 'assistant',
          text: userFriendlyMessage || `I encountered an error processing your message: ${errorMessage}`,
          timestamp: new Date().toISOString(),
          type: 'error',
        };
        setMessages(prev => [...prev, errorChatMessage]);
        throw error;
      } finally {
        setIsTyping(false);
      }
    } else {
      // Sample mode: use placeholder response
      if (!text.startsWith('/')) {
        setIsTyping(true);
        setTimeout(async () => {
          const assistantMessage: Omit<ChatMessage, 'id' | 'timestamp'> = {
            threadId: effectiveThreadId,
            who: 'assistant',
            text: `I received your message: "${text}". This is a placeholder response.`,
            type: 'text',
          };
          addMessage(assistantMessage);
          setIsTyping(false);
        }, 1000);
      }
    }
  }, [dataSource, addMessage, user, chatUIRole, updateThread]);

  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  const retryMessage = useCallback(async (messageId: string) => {
    // Implementation for retrying failed messages
    console.log('Retrying message:', messageId);
  }, []);

  const updateThreadContext = useCallback((context: Partial<ChatThread['context']>) => {
    if (!currentThreadId) return;
    
    const currentThread = threads.find(t => t.id === currentThreadId);
    if (currentThread) {
      const updatedContext = { ...currentThread.context, ...context };
      void updateThread(currentThreadId, { context: updatedContext });
    }
  }, [currentThreadId, threads, updateThread]);

  const getCurrentContext = useCallback(() => {
    const currentThread = threads.find(t => t.id === currentThreadId);
    return currentThread?.context || null;
  }, [threads, currentThreadId]);

  const searchMessages = useCallback((query: string): ChatMessage[] => {
    return messages.filter(msg => 
      msg.text.toLowerCase().includes(query.toLowerCase())
    );
  }, [messages]);

  const searchThreads = useCallback((query: string): ChatThread[] => {
    return threads.filter(thread => 
      thread.name.toLowerCase().includes(query.toLowerCase()) ||
      thread.description?.toLowerCase().includes(query.toLowerCase())
    );
  }, [threads]);

  const markAsRead = useCallback((threadId: string) => {
    // Implementation for marking messages as read
    console.log('Marking thread as read:', threadId);
  }, []);

  const subscribeToUpdates = useCallback(() => {
    // WebSocket connection for real-time updates
    const wsUrl = `${WS_BASE_URL}/ws/chat`;
    wsRef.current = new WebSocket(wsUrl);
    
    wsRef.current.onmessage = (event) => {
      const data = JSON.parse(event.data);
      // Handle real-time updates
      console.log('Received real-time update:', data);
    };
    
    wsRef.current.onclose = () => {
      // Reconnect after 3 seconds
      reconnectTimeoutRef.current = setTimeout(() => {
        subscribeToUpdates();
      }, 3000);
    };
  }, []);

  const unsubscribeFromUpdates = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      unsubscribeFromUpdates();
    };
  }, [unsubscribeFromUpdates]);

  // Memoize context value to prevent unnecessary re-renders
  const value: ChatContextType = useMemo(() => ({
    threads,
    currentThreadId,
    messages,
    setMessages,
    isLoading,
    isLoadingMessages,
    isTyping,
    error,
    unreadCount,
    createThread,
    selectThread,
    deleteThread,
    updateThread,
    pinThread,
    unpinThread,
    loadUserThreads,
    loadMessages,
    sendMessage,
    addMessage,
    clearMessages,
    retryMessage,
    updateThreadContext,
    getCurrentContext,
    searchMessages,
    searchThreads,
    markAsRead,
    subscribeToUpdates,
    unsubscribeFromUpdates,
  }), [
    threads,
    currentThreadId,
    messages,
    setMessages,
    isLoading,
    isLoadingMessages,
    isTyping,
    error,
    unreadCount,
    createThread,
    selectThread,
    deleteThread,
    updateThread,
    pinThread,
    unpinThread,
    loadUserThreads,
    loadMessages,
    sendMessage,
    addMessage,
    clearMessages,
    retryMessage,
    updateThreadContext,
    getCurrentContext,
    searchMessages,
    searchThreads,
    markAsRead,
    subscribeToUpdates,
    unsubscribeFromUpdates,
  ]);

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = (): ChatContextType => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};
